import { alpha, Chip, styled } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import RemoveCircleOutlineOutlinedIcon from "@mui/icons-material/RemoveCircleOutlineOutlined";

export type ChipStatus = "success" | "warning" | "error" | "info" | "primary";

const SuccessChip = styled(Chip)(({ theme }) => ({
	backgroundColor: alpha(theme.palette.success.main, 0.1),
}));

const WarningChip = styled(Chip)(({ theme }) => ({
	backgroundColor: alpha(theme.palette.warning.main, 0.1),
}));

const ErrorChip = styled(Chip)(({ theme }) => ({
	backgroundColor: alpha(theme.palette.error.main, 0.1),
}));

const InfoChip = styled(Chip)(({ theme }) => ({
	backgroundColor: alpha(theme.palette.info.main, 0.1),
}));

const PrimaryChip = styled(Chip)(({ theme }) => ({
	backgroundColor: alpha(theme.palette.primary.main, 0.1),
}));

const PulsatingIcon = styled(CircleOutlinedIcon)({
	"@keyframes pulsate": {
		"0%": {
			opacity: 1,
		},
		"50%": {
			opacity: 0.2,
		},
		"100%": {
			opacity: 1,
		},
	},
	animation: "pulsate 1.5s ease-in-out infinite",
});

interface StatusChipProps {
	readonly label: string;
	readonly status: ChipStatus;
	readonly loading?: boolean;
	readonly icon?: boolean;
	readonly onClick?: () => void;
}

export const StatusChip = ({ label, status, loading = false, icon = true, onClick }: StatusChipProps) => {
	if (loading) {
		return <InfoChip label={label} icon={icon ? <PulsatingIcon color="info" /> : undefined} />;
	}

	switch (status) {
		case "success":
			return (
				<SuccessChip
					label={label}
					icon={icon ? <CheckCircleOutlineIcon color="success" /> : undefined}
					onClick={onClick}
				/>
			);
		case "warning":
			return (
				<WarningChip
					label={label}
					icon={icon ? <ErrorOutlineOutlinedIcon color="warning" /> : undefined}
					onClick={onClick}
				/>
			);
		case "error":
			return (
				<ErrorChip
					label={label}
					icon={icon ? <ErrorOutlineOutlinedIcon color="error" /> : undefined}
					onClick={onClick}
				/>
			);
		case "info":
			return (
				<InfoChip
					label={label}
					icon={icon ? <RemoveCircleOutlineOutlinedIcon color="info" /> : undefined}
					onClick={onClick}
				/>
			);
		case "primary":
			return (
				<PrimaryChip
					label={label}
					icon={icon ? <RemoveCircleOutlineOutlinedIcon color="primary" /> : undefined}
					onClick={onClick}
				/>
			);
	}
};
